import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Filter, DollarSign, Grid3x3, Check } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { PricingFilter } from '../hooks/useFilters';
interface FilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    pricing: PricingFilter[];
    categories: string[];
    minRating: number;
    features: string[];
  };
  availableCategories: string[];
  togglePricing: (pricing: PricingFilter) => void;
  toggleCategory: (category: string) => void;
  setMinRating: (rating: number) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  filteredCount: number;
  totalCount: number;
}
export function FilterSidebar({
  isOpen,
  onClose,
  filters,
  availableCategories,
  togglePricing,
  toggleCategory,
  setMinRating,
  clearFilters,
  activeFilterCount,
  filteredCount,
  totalCount
}: FilterSidebarProps) {
  const pricingOptions: {
    value: PricingFilter;
    label: string;
    icon: string;
  }[] = [{
    value: 'free',
    label: 'Free',
    icon: '🎁'
  }, {
    value: 'freemium',
    label: 'Freemium',
    icon: '⚡'
  }, {
    value: 'paid',
    label: 'Paid',
    icon: '💎'
  }];
  return <AnimatePresence data-id="element-281">
      {isOpen && <>
          {/* Mobile Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40" data-id="element-282" />

          {/* Sidebar */}
          <motion.div initial={{
        x: -320
      }} animate={{
        x: 0
      }} exit={{
        x: -320
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed left-0 top-0 h-full w-80 bg-black/95 backdrop-blur-xl border-r border-white/10 z-50 overflow-y-auto" data-id="element-283">
            <div className="p-6 space-y-8" data-id="element-284">
              {/* Header */}
              <div className="flex items-center justify-between" data-id="element-285">
                <div className="flex items-center gap-3" data-id="element-286">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30" data-id="element-287">
                    <Filter size={20} className="text-cyan-400" data-id="element-288" />
                  </div>
                  <div data-id="element-289">
                    <h2 className="text-lg font-bold text-white" data-id="element-290">Filters</h2>
                    {activeFilterCount > 0 && <p className="text-xs text-gray-400" data-id="element-291">
                        {activeFilterCount} active
                      </p>}
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all" data-id="element-292">
                  <X size={18} className="text-gray-400 hover:text-white" data-id="element-293" />
                </button>
              </div>

              {/* Results Count */}
              <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="p-4 rounded-lg bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/20" data-id="element-294">
                <div className="text-2xl font-bold text-white mb-1" data-id="element-295">
                  {filteredCount}
                  <span className="text-gray-400 text-sm ml-2" data-id="element-296">
                    / {totalCount} tools
                  </span>
                </div>
                <p className="text-xs text-gray-400" data-id="element-297">Matching your filters</p>
              </motion.div>

              {/* Clear Filters Button */}
              {activeFilterCount > 0 && <motion.button initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} onClick={clearFilters} className="w-full px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-red-500/30 text-white font-medium transition-all text-sm flex items-center justify-center gap-2" data-id="element-298">
                  <X size={16} data-id="element-299" />
                  Clear All Filters
                </motion.button>}

              {/* Pricing Filter */}
              <div data-id="element-300">
                <div className="flex items-center gap-2 mb-3" data-id="element-301">
                  <DollarSign size={16} className="text-gray-400" data-id="element-302" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider" style={{
                fontFamily: 'Space Grotesk, sans-serif'
              }} data-id="element-303">
                    Pricing Model
                  </h3>
                </div>
                <div className="space-y-2" data-id="element-304">
                  {pricingOptions.map(option => <motion.button key={option.value} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => togglePricing(option.value)} className={`w-full p-3 rounded-lg border transition-all text-left flex items-center justify-between ${filters.pricing.includes(option.value) ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:border-white/20'}`} data-id="element-305">
                      <div className="flex items-center gap-3" data-id="element-306">
                        <span className="text-xl" data-id="element-307">{option.icon}</span>
                        <span className="font-medium" data-id="element-308">{option.label}</span>
                      </div>
                      {filters.pricing.includes(option.value) && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 500
                }} data-id="element-309">
                          <Check size={18} className="text-green-400" data-id="element-310" />
                        </motion.div>}
                    </motion.button>)}
                </div>
              </div>

              {/* Categories Filter */}
              <div data-id="element-311">
                <div className="flex items-center gap-2 mb-3" data-id="element-312">
                  <Grid3x3 size={16} className="text-gray-400" data-id="element-313" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider" style={{
                fontFamily: 'Space Grotesk, sans-serif'
              }} data-id="element-314">
                    Categories
                  </h3>
                  {filters.categories.length > 0 && <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30" data-id="element-315">
                      {filters.categories.length}
                    </span>}
                </div>
                <div className="relative" data-id="element-316">
                  {/* Scrollable categories */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 pt-1 pb-1 custom-scrollbar-grey" data-id="element-317">
                    {availableCategories.map(category => <motion.button key={category} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => toggleCategory(category)} className={`w-full p-2.5 rounded-lg border transition-all text-left text-sm font-medium ${filters.categories.includes(category) ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`} data-id="element-318">
                        <div className="flex items-center justify-between" data-id="element-319">
                          <span data-id="element-320">{category}</span>
                          {filters.categories.includes(category) && <motion.div initial={{
                      scale: 0,
                      rotate: -180
                    }} animate={{
                      scale: 1,
                      rotate: 0
                    }} transition={{
                      type: 'spring',
                      stiffness: 500
                    }} data-id="element-321">
                              <Check size={16} className="text-green-400" data-id="element-322" />
                            </motion.div>}
                        </div>
                      </motion.button>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}